import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  alpha,
  Autocomplete,
  useTheme,
  Paper,
  Divider,
  Chip,
  Select,
  CircularProgress,
  useMediaQuery,
  Breadcrumbs,
  Link,
  Drawer,
  AppBar,
  Toolbar,
} from "@mui/material";
import {
  Add as PlusIcon,
  Delete as TrashIcon,
  Save as SaveIcon,
  ZoomIn as ZoomIcon,
  Settings as SettingsIcon,
  Accessible as AccessibleIcon,
  Close as CloseIcon,
  ViewQuilt as GridIcon,
  Dashboard as ClassicIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  ContentCopy as DuplicateIcon,
  FormatListNumbered as RenumberIcon,
  TouchApp as SelectionIcon,
  ChevronLeft as BackIcon,
  ChevronRight as ChevronRightIcon,
  Layers as LayersIcon,
  Assessment as StatsIcon,
  HelpOutline as HelpIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  InfoOutlined as InfoIcon,
  VisibilityOutlined as ViewIcon,
} from "@mui/icons-material";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import api from "../../services/api";
import ModernLoader from "../common/ModernLoader";

// --- Static Helper Functions (Outside Component) ---

const getRowLetter = (secIdx, rowIdx, sections) => {
  let rowsBeforeThis = 0;
  for (let i = 0; i < secIdx; i++) {
    if (sections[i]) rowsBeforeThis += sections[i].rows.length;
  }
  rowsBeforeThis += rowIdx;
  let n = rowsBeforeThis + 1;
  let rowLetter = "";
  while (n > 0) {
    let mod = (n - 1) % 26;
    rowLetter = String.fromCharCode(65 + mod) + rowLetter;
    n = Math.floor((n - 1) / 26);
  }
  return rowLetter;
};

const recalculateRowNumbers = (rowArray) => {
  let counter = 1;
  return rowArray.map(seat => {
    if (seat === null) return null;
    if (seat >= 1000) return 1000 + (counter++);
    return counter++;
  });
};

const SectionCard = React.memo(({
  section,
  secIndex,
  sections,
  setSections,
  selectedSeats,
  selectionMode,
  cycleSeat,
  appendSeat,
  removeRow,
  addRow,
  removeSection
}) => {
  return (
    <Box sx={{
      mb: { xs: 4, md: 8 },
      width: '100%',
      maxWidth: 1000,
      bgcolor: '#fff',
      p: { xs: 3, md: 5 },
      borderRadius: "28px",
      textAlign: 'center',
      transition: 'all 0.3s ease',
      border: '1.5px solid rgba(0,0,0,0.03)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
      '&:hover': {
        boxShadow: '0 20px 60px rgba(0,0,0,0.06)',
        borderColor: 'rgba(0,0,0,0.08)'
      }
    }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center" justifyContent="center" sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 900, color: 'primary.main', opacity: 0.6 }}>NAME</Typography>
          <Autocomplete
            freeSolo
            options={["VIP", "EXECUTIVE", "CLUB", "RECLINER", "ROYAL", "PREMIUM", "ECONOMY"]}
            value={section.title}
            onInputChange={(e, val) => setSections(prev => prev.map((s, i) => i === secIndex ? { ...s, title: val } : s))}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="e.g. VIP LOUNGE"
                onMouseDown={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: 'rgba(0,0,0,0.02)',
                    fontWeight: 800,
                    fontSize: '14px',
                    width: 220,
                    '& fieldset': { border: 'none' },
                    '&:hover fieldset': { border: 'none' },
                    '&.Mui-focused fieldset': { border: '1px solid rgba(0,0,0,0.1)' }
                  }
                }}
              />
            )}
            sx={{ width: 220 }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 900, color: 'primary.main', opacity: 0.6 }}>TIER</Typography>
          <Select
            size="small"
            value={section.class}
            onMouseDown={e => e.stopPropagation()}
            onChange={e => {
              const val = e.target.value;
              setSections(prev => prev.map((s, i) => i === secIndex ? { ...s, class: val } : s));
            }}
            sx={{
              borderRadius: '12px',
              bgcolor: 'rgba(0,0,0,0.02)',
              fontWeight: 800,
              fontSize: '13px',
              minWidth: 120,
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
            }}
          >
            <MenuItem value="prime">Premium</MenuItem>
            <MenuItem value="gold">Gold</MenuItem>
            <MenuItem value="silver">Silver</MenuItem>
          </Select>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 900, color: 'primary.main', opacity: 0.6 }}>PRICE</Typography>
          <TextField
            type="number"
            value={section.price}
            onMouseDown={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            onChange={e => {
              const val = Number(e.target.value) || 0;
              setSections(prev => prev.map((s, i) => i === secIndex ? { ...s, price: val } : s));
            }}
            InputProps={{
              startAdornment: <Typography sx={{ fontSize: '13px', fontWeight: 900, mr: 1, opacity: 0.4 }}>₹</Typography>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: 'rgba(0,0,0,0.02)',
                fontWeight: 800,
                fontSize: '14px',
                width: 120,
                '& fieldset': { border: 'none' }
              }
            }}
          />
        </Box>

        <Tooltip title="Remove Section">
          <IconButton
            onClick={() => removeSection(secIndex)}
            sx={{
              bgcolor: 'rgba(255,0,0,0.05)',
              color: 'error.main',
              '&:hover': { bgcolor: 'error.main', color: '#fff' }
            }}
          >
            <TrashIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box sx={{ overflowX: 'auto', pb: 2, width: '100%', bgcolor: 'rgba(0,0,0,0.01)', borderRadius: '20px', p: 3 }}>
        <Stack spacing={2.5} alignItems="center" sx={{ minWidth: 'fit-content' }}>
          {section.rows.map((row, rIdx) => (
            <Stack key={rIdx} direction="row" spacing={0} alignItems="center">
              <Typography sx={{ width: 40, textAlign: 'center', fontWeight: 900, fontSize: '14px', color: 'rgb(119, 119, 126)', mr: { xs: 2, md: 5 } }}>
                {getRowLetter(secIndex, rIdx, sections)}
              </Typography>

              <Stack direction="row" spacing={1.2} sx={{ mr: { xs: 2, md: 5 } }}>
                {row.map((seat, sIdx) => {
                  const isSelected = selectedSeats.has(`${secIndex}-${rIdx}-${sIdx}`);
                  const isEmpty = seat === null;
                  const isAccessible = seat >= 1000;
                  const seatNum = isAccessible ? seat - 1000 : seat;

                  return (
                    <Box
                      key={sIdx}
                      onClick={() => cycleSeat(secIndex, rIdx, sIdx)}
                      sx={{
                        width: { xs: 24, md: 34 }, height: { xs: 24, md: 34 }, borderRadius: '8px', cursor: 'pointer',
                        border: isSelected ? '0' : (isEmpty ? '1.5px dashed rgba(0,0,0,0.1)' : '1px solid #18181b'),
                        bgcolor: isSelected ? '#18181b' : (isAccessible ? 'rgba(0,0,0,0.03)' : (isEmpty ? 'transparent' : '#fff')),
                        color: isSelected ? '#fff' : (isEmpty ? 'transparent' : '#18181b'),
                        outline: isSelected ? 'rgba(0,0,0,0.1) solid 2px' : 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: { xs: '9px', md: '12px' }, fontWeight: 900,
                        transition: 'all 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'scale(1.2)',
                          zIndex: 10,
                          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          opacity: 1
                        },
                        opacity: isEmpty ? 0.3 : 1,
                        pointerEvents: 'auto'
                      }}
                    >
                      {isEmpty ? '·' : (isAccessible ? `♿${seatNum}` : seat)}
                    </Box>
                  );
                })}
              </Stack>

              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={() => appendSeat(secIndex, rIdx)} sx={{ color: 'rgba(0,0,0,0.15)', '&:hover': { color: 'primary.main' } }}><PlusIcon sx={{ fontSize: 16 }} /></IconButton>
                <IconButton size="small" onClick={() => removeRow(secIndex, rIdx)} sx={{ color: 'rgba(0,0,0,0.15)', '&:hover': { color: 'error.main' } }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
              </Stack>
            </Stack>
          ))}
          <Button
            size="small" variant="text"
            onClick={() => addRow(secIndex)}
            startIcon={<PlusIcon fontSize="small" />}
            sx={{
              color: 'rgba(0,0,0,0.3)',
              fontSize: '11px',
              fontWeight: 900,
              mt: 1,
              letterSpacing: '0.1em',
              '&:hover': { color: 'primary.main', bgcolor: 'transparent' }
            }}
          >
            APPEND SEAT SECTOR
          </Button>
        </Stack>
      </Box>
    </Box>
  );
});

// --- Main Builder Component ---

const SeatLayoutBuilder = () => {
  const theme = useTheme();
  const { theaterId, screenId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isPartner = location.pathname.startsWith("/partner");
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { user: clerkUser } = useUser();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [screenData, setScreenData] = useState(null);
  const [sections, setSections] = useState([]);
  const [viewMode, setViewMode] = useState("figma");
  const [selectedSeats, setSelectedSeats] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const calculateStats = useMemo(() => {
    let seats = 0;
    let gaps = 0;
    let totalValue = 0;
    sections.forEach(sec => {
      sec.rows.forEach(row => {
        row.forEach(s => {
          if (s === null) gaps++;
          else {
            seats++;
            totalValue += (Number(sec.price) || 0);
          }
        });
      });
    });
    return { seats, gaps, totalValue };
  }, [sections]);

  const toggleSeatSelection = useCallback((secIdx, rowIdx, seatIdx) => {
    const key = `${secIdx}-${rowIdx}-${seatIdx}`;
    setSelectedSeats(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isPartner) {
          if (clerkUser?.id) {
            const activeTheaterId = theaterId || localStorage.getItem('activeTheaterId');
            const res = await api.get(`/partner/screens/${clerkUser.id}`, {
              params: { theaterId: activeTheaterId }
            });
            // The backend returns { screens: [...], theaterId: ... }
            const screensList = res.data?.screens || [];
            const screen = screensList.find(s => s._id === screenId);
            if (screen) setScreenData(screen);
          }
        } else {
          const res = await api.get(`/theaters/${theaterId}`);
          if (res.data?.screens) {
            const data = res.data.screens.find(s => s._id === screenId);
            if (data) setScreenData(data);
          }
        }
      } catch (error) {
        toast.error("Cloud Sync Error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [theaterId, screenId, isPartner, clerkUser?.id]);

  useEffect(() => {
    if (screenData?.seatLayout && screenData.seatLayout.length > 0) {
      const grouped = groupLayoutIntoSections(screenData.seatLayout);
      setSections(grouped);
      updateHistory(grouped);
    } else if (screenData) {
      const defaultSections = [{
        id: Math.random().toString(36).substr(2, 9),
        title: "PREMIUM", class: "prime", price: 350,
        rows: [[1, 2, 3, 4, 5, null, 6, 7, 8, 9, 10]]
      }];
      setSections(defaultSections);
      updateHistory(defaultSections);
    }
  }, [screenData]);

  const groupLayoutIntoSections = (layout) => {
    const grouped = [];
    let currentSection = null;
    layout.forEach((row) => {
      const sectionLabel = row.section || "DEFAULT";
      if (!currentSection || currentSection.title !== sectionLabel) {
        currentSection = {
          id: Math.random().toString(36).substr(2, 9),
          title: sectionLabel, class: row.class || "classic",
          price: row.price || 150, rows: [],
        };
        grouped.push(currentSection);
      }
      currentSection.rows.push([...row.seats]);
    });
    return grouped;
  };

  const updateHistory = (newSections) => {
    setHistory(prev => {
      const next = prev.slice(0, historyIndex + 1);
      next.push(JSON.stringify(newSections));
      if (next.length > 20) next.shift();
      return next;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 19));
  };

  const undo = () => {
    if (historyIndex > 0) {
      const prev = JSON.parse(history[historyIndex - 1]);
      setSections(prev);
      setHistoryIndex(historyIndex - 1);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const next = JSON.parse(history[historyIndex + 1]);
      setSections(next);
      setHistoryIndex(historyIndex + 1);
    }
  };

  const addSection = () => {
    const newSecs = [...sections, {
      id: Math.random().toString(36).substr(2, 9),
      title: "NEW SECTION", class: "classic", price: 200,
      rows: [[1, 2, 3, 4, 5, null, 6, 7, 8, 9, 10]],
    }];
    setSections(newSecs);
    updateHistory(newSecs);
  };

  const removeSection = useCallback((secIndex) => {
    setSections(prev => {
      const next = [...prev];
      next.splice(secIndex, 1);
      updateHistory(next);
      return next;
    });
  }, [historyIndex, history]);

  const addRow = useCallback((secIndex) => {
    setSections(prev => {
      const next = [...prev];
      next[secIndex] = { ...next[secIndex], rows: [...next[secIndex].rows, [1, 2, 3, 4, 5, null, 6, 7, 8, 9, 10]] };
      updateHistory(next);
      return next;
    });
  }, [historyIndex, history]);

  const removeRow = useCallback((secIndex, rowIndex) => {
    setSections(prev => {
      const next = [...prev];
      const newRows = [...next[secIndex].rows];
      newRows.splice(rowIndex, 1);
      next[secIndex] = { ...next[secIndex], rows: newRows };
      updateHistory(next);
      return next;
    });
  }, [historyIndex, history]);

  const appendSeat = useCallback((secIndex, rowIndex, gap = false) => {
    setSections(prev => {
      const next = [...prev];
      const newRow = [...next[secIndex].rows[rowIndex], gap ? null : 1];
      next[secIndex].rows[rowIndex] = recalculateRowNumbers(newRow);
      updateHistory(next);
      return next;
    });
  }, [historyIndex, history]);

  const cycleSeat = useCallback((secIndex, rowIndex, seatIndex) => {
    if (selectionMode) {
      toggleSeatSelection(secIndex, rowIndex, seatIndex);
      return;
    }

    setSections(prev => {
      const next = prev.map((sec, sIdx) => {
        if (sIdx !== secIndex) return sec;
        const newRows = sec.rows.map((row, rIdx) => {
          if (rIdx !== rowIndex) return row;
          const updatedRow = [...row];
          const val = updatedRow[seatIndex];
          if (val !== null && val < 1000) updatedRow[seatIndex] = null;
          else if (val === null) updatedRow[seatIndex] = 1001;
          else updatedRow[seatIndex] = 1;
          return recalculateRowNumbers(updatedRow);
        });
        return { ...sec, rows: newRows };
      });
      updateHistory(next);
      return next;
    });
  }, [selectionMode, toggleSeatSelection, historyIndex, history]);

  const bulkUpdateSeats = (type) => {
    setSections(prev => {
      const next = prev.map((sec, sIdx) => {
        const newRows = sec.rows.map((row, rIdx) => {
          return row.map((seat, seatIdx) => {
            if (selectedSeats.has(`${sIdx}-${rIdx}-${seatIdx}`)) {
              if (type === 'active') return 1;
              if (type === 'gap') return null;
              if (type === 'accessible') return 1001;
            }
            return seat;
          });
        });

        const renumberedRows = newRows.map(r => recalculateRowNumbers(r));
        return { ...sec, rows: renumberedRows };
      });
      setSelectedSeats(new Set());
      updateHistory(next);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const flattened = [];
      sections.forEach((sec, secIdx) => {
        sec.rows.forEach((seats, rIdx) => {
          flattened.push({
            row: getRowLetter(secIdx, rIdx, sections),
            seats: [...seats],
            class: sec.class,
            section: sec.title.toUpperCase(),
            price: Number(sec.price) || 0,
          });
        });
      });

      const effectiveTheaterId = theaterId || localStorage.getItem('activeTheaterId');
      const endpoint = isPartner
        ? `/partner/screens/${clerkUser?.id}/${screenId}/layout`
        : `/theaters/${effectiveTheaterId}/screens/${screenId}/layout`;

      if (isPartner) await api.patch(endpoint, { seatLayout: flattened, theaterId: effectiveTheaterId });
      else await api.put(endpoint, { seatLayout: flattened });

      toast.success("Blueprint Synced Successfully");
    } catch (error) {
      toast.error("Cloud Sync Failed");
    } finally {
      setSaving(false);
    }
  };

  const MemoizedSections = useMemo(() => {
    return sections.map((sec, idx) => (
      <SectionCard
        key={sec.id} section={sec} secIndex={idx} sections={sections} setSections={setSections}
        selectedSeats={selectedSeats} selectionMode={selectionMode} cycleSeat={cycleSeat}
        appendSeat={appendSeat} removeRow={removeRow} addRow={addRow} removeSection={removeSection}
      />
    ));
  }, [sections, selectedSeats, selectionMode, cycleSeat, appendSeat, removeRow, addRow, removeSection]);

  if (loading) return <ModernLoader text="Syncing Architectural Frequencies..." />;

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", bgcolor: "#09090b", color: "#fff", overflow: "hidden" }}>
      <AppBar position="static" elevation={0} sx={{ bgcolor: "#09090b", borderBottom: "1px solid rgba(255,255,255,0.08)", zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ justifyContent: "space-between", height: 72 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton onClick={() => navigate(-1)} color="inherit"><BackIcon /></IconButton>
            <Divider orientation="vertical" flexItem sx={{ bgcolor: "rgba(255,255,255,0.1)", my: 2 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: -1, lineHeight: 1 }}>ARCHITECT STUDIO</Typography>
              <Typography variant="caption" sx={{ opacity: 0.5, fontWeight: 600, textTransform: "uppercase" }}>
                {isPartner ? "Partner Access" : "Admin Control"} • {screenData?.screenNumber || "S1"}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.5} sx={{ p: 0.5, bgcolor: "rgba(255,255,255,0.04)", borderRadius: "14px", border: "1px solid rgba(255,255,255,0.06)" }}>
            <Button size="small" onClick={() => setViewMode("figma")} variant={viewMode === "figma" ? "contained" : "text"} sx={{ borderRadius: "10px", px: 2, bgcolor: viewMode === "figma" ? "#fff" : "transparent", color: viewMode === "figma" ? "#000" : "inherit" }}>Design</Button>
            <Button size="small" onClick={() => setViewMode("preview")} variant={viewMode === "preview" ? "contained" : "text"} sx={{ borderRadius: "10px", px: 2, bgcolor: viewMode === "preview" ? "#fff" : "transparent", color: viewMode === "preview" ? "#000" : "inherit" }}>Preview</Button>
            <Button size="small" onClick={() => setViewMode("classic")} variant={viewMode === "classic" ? "contained" : "text"} sx={{ borderRadius: "10px", px: 2, bgcolor: viewMode === "classic" ? "#fff" : "transparent", color: viewMode === "classic" ? "#000" : "inherit" }}>Registry</Button>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack direction="row" spacing={0.5}>
              <IconButton color="inherit" onClick={undo} disabled={historyIndex <= 0} sx={{ opacity: historyIndex <= 0 ? 0.3 : 1 }}><UndoIcon fontSize="small" /></IconButton>
              <IconButton color="inherit" onClick={redo} disabled={historyIndex >= history.length - 1} sx={{ opacity: historyIndex >= history.length - 1 ? 0.3 : 1 }}><RedoIcon fontSize="small" /></IconButton>
            </Stack>
            <Button variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />} onClick={handleSave} disabled={saving} sx={{ borderRadius: "12px", px: 3, height: 44, fontWeight: 900, bgcolor: "#fff", color: "#18181b", "&:hover": { bgcolor: "#e4e4e7" } }}>{saving ? "Syncing..." : "Commit Changes"}</Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box sx={{ flexGrow: 1, overflow: "hidden", position: "relative", bgcolor: "#f4f4f5" }}>
        {viewMode === 'classic' ? (
          <Box sx={{ p: 4, height: '100%', overflow: 'auto' }}><Paper sx={{ p: 3, bgcolor: '#0b0b0d', color: '#00ff41', fontFamily: '"Fira Code", monospace', borderRadius: '16px' }}><pre style={{ fontSize: '12px' }}>{JSON.stringify(sections, null, 2)}</pre></Paper></Box>
        ) : viewMode === 'preview' ? (
          <Box sx={{ height: '100%', width: '100%', bgcolor: '#fff', overflow: 'auto' }}>
            <Box sx={{ p: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="overline" sx={{ fontWeight: 900, mb: 6, color: 'rgba(0,0,0,0.2)', letterSpacing: 6 }}>SIMULATED AUDITORIUM SCALE</Typography>
              {sections.map(sec => (
                <Box key={sec.id} sx={{ mb: 6, textAlign: 'center', width: 'fit-content' }}>
                  <Typography sx={{ fontSize: '12px', color: 'rgb(119, 119, 126)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 3 }}>{sec.class.toUpperCase()} - {sec.title} : ₹{sec.price}</Typography>
                  {sec.rows.map((row, rIdx) => (
                    <Stack key={rIdx} direction="row" spacing={0} alignItems="center" mb={1.5}>
                      <Typography sx={{ width: 35, textAlign: 'center', fontSize: '14px', color: 'rgb(119, 119, 126)', fontWeight: 500, mr: 4 }}>{getRowLetter(sections.indexOf(sec), rIdx, sections)}</Typography>
                      <Stack direction="row" spacing={1}>
                        {row.map((seat, sIdx) => {
                          const isAccessible = seat >= 1000;
                          const seatNum = isAccessible ? seat - 1000 : seat;
                          return (
                            <Box key={sIdx} sx={{ width: 32, height: 32, borderRadius: '8px', border: seat === null ? 'none' : '1px solid #18181b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, color: '#18181b', visibility: seat === null ? 'hidden' : 'visible' }}>
                              {seat === null ? '' : (isAccessible ? `♿${seatNum}` : seat)}
                            </Box>
                          );
                        })}
                      </Stack>
                    </Stack>
                  ))}
                </Box>
              ))}
              <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 10 }}>
                <img src="https://cdn.district.in/movies-web/_next/static/media/screen-img-light.b7b18ffd.png" alt="Screen" style={{ width: '450px', opacity: 0.8 }} />
                <Typography sx={{ color: 'rgb(139, 139, 147)', fontSize: '11px', fontWeight: 700, letterSpacing: '4px', textTransform: 'uppercase', mt: 1.5 }}>FRONT OF HOUSE</Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <TransformWrapper centerOnInit initialScale={0.8} minScale={0.1} maxScale={4} limitToBounds={false}>
            <TransformComponent wrapperStyle={{ width: "100%", height: "100%", backgroundColor: "#f4f4f5" }} contentStyle={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Box sx={{ padding: "100vh 100vw", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box sx={{ bgcolor: "#fff", p: 10, borderRadius: "32px", boxShadow: "0 20px 80px rgba(0,0,0,0.05)", display: "flex", flexDirection: "column", alignItems: "center", minWidth: "fit-content", border: "1px solid rgba(0,0,0,0.03)" }}>
                  <Box sx={{ mb: 10, width: "100%", textAlign: "center" }}>
                    <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: -1, color: "#18181b", mb: 1 }}>{screenData?.name || "Auditorium"}</Typography>
                    <Typography variant="body2" sx={{ color: "rgb(119, 119, 126)", fontWeight: 500 }}>Architectural Blueprint & Orchestration</Typography>
                  </Box>
                  {MemoizedSections}
                  <Button variant="contained" size="large" onClick={addSection} startIcon={<PlusIcon />} sx={{ mt: 8, borderRadius: "16px", px: 6, py: 2, bgcolor: "#18181b", color: "#fff", fontWeight: 800, "&:hover": { bgcolor: "#000" } }}>Initialize New Sector</Button>
                  <Box sx={{ mt: 15, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <img src="https://cdn.district.in/movies-web/_next/static/media/screen-img-light.b7b18ffd.png" alt="Screen" style={{ width: "600px", opacity: 0.8 }} />
                    <Typography sx={{ color: "rgb(139, 139, 147)", fontSize: "12px", fontWeight: 700, letterSpacing: "8px", textTransform: "uppercase", mt: 2 }}>ORCHESTRAL SCREEN PLANE</Typography>
                  </Box>
                </Box>
              </Box>
            </TransformComponent>
          </TransformWrapper>
        )}

        <AnimatePresence>
          {selectedSeats.size > 1 && (
            <motion.div initial={{ y: 100, x: "-50%", opacity: 0 }} animate={{ y: 0, x: "-50%", opacity: 1 }} exit={{ y: 100, x: "-50%", opacity: 0 }} style={{ position: "fixed", bottom: 40, left: "50%", zIndex: 1000 }}>
              <Paper elevation={24} sx={{ p: 1.5, borderRadius: "20px", display: "flex", alignItems: "center", gap: 1.5, bgcolor: "#18181b", color: "#fff", border: "1px solid rgba(255,255,255,0.12)", backdropFilter: "blur(20px)" }}>
                <Box sx={{ ml: 2, mr: 1, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: '#fff', lineHeight: 1 }}>{selectedSeats.size}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '8px', fontWeight: 900, textTransform: 'uppercase' }}>Selected</Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ bgcolor: "rgba(255,255,255,0.1)", mx: 1 }} />
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" size="small" onClick={() => bulkUpdateSeats("active")} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', borderRadius: '10px', fontWeight: 700 }}>Active</Button>
                  <Button variant="contained" size="small" onClick={() => bulkUpdateSeats("gap")} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', borderRadius: '10px', fontWeight: 700 }}>Void</Button>
                  <Button variant="contained" size="small" onClick={() => bulkUpdateSeats("accessible")} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', borderRadius: '10px', fontWeight: 700 }}>Accessible</Button>
                </Stack>
                <Divider orientation="vertical" flexItem sx={{ bgcolor: "rgba(255,255,255,0.1)", mx: 1 }} />
                <IconButton size="small" onClick={() => setSelectedSeats(new Set())} sx={{ color: "rgba(255,255,255,0.4)" }}><CloseIcon fontSize="small" /></IconButton>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        <Box sx={{ position: "fixed", bottom: 40, right: 40, zIndex: 1000, display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={() => setSelectionMode(!selectionMode)} startIcon={<SelectionIcon />} sx={{ height: 56, borderRadius: "28px", px: 3, bgcolor: selectionMode ? "#fff" : "rgba(24, 24, 27, 0.8)", color: selectionMode ? "#000" : "#fff", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.1)", fontWeight: 900, letterSpacing: '0.1em' }}>{selectionMode ? "Exit Selection" : "Bulk Selection"}</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SeatLayoutBuilder;